'use client';

import {
  FC,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Button, ClickableDiv } from '../button';
import { cn } from '../../lib/lib';
import { createPortal } from 'react-dom';
import { useMutationObserver } from '../../hooks/use-element-observer';
import { Slot } from '@radix-ui/react-slot';

export const ShowcaseButton: FC<{
  children: React.ReactNode;
  onClick?: () => void;
}> = ({ children, onClick }) => {
  return (
    <Button
      variant="none"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      className="drop-shadow-card-low px-0 italic text-[#FFA800]"
    >
      {children}
    </Button>
  );
};

export const DEFAULT_SHOWCASE_STEP_CONTENT_WIDTH = 264;
export const ShowcaseStepContent: FC<{
  content?: ReactNode;
  nextButton?: ReactNode;
  backButton?: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  onClose?: () => void;
  width?: number;
}> = ({
  content,
  nextButton,
  backButton,
  className,
  style,
  onClose,
  width = DEFAULT_SHOWCASE_STEP_CONTENT_WIDTH,
}) => {
  return createPortal(
    <div
      className={cn(
        'z-showcaseStep pointer-events-auto fixed italic',
        className
      )}
      style={style}
    >
      <div
        className={cn('rounded-6 relative bg-[#5B639E] px-6 py-4', className)}
        style={{
          width,
        }}
      >
        <div className="text-sm text-white">{content}</div>
        <div className="flex flex-row justify-between">
          <div>{backButton}</div>
          <div>{nextButton}</div>
        </div>
        {onClose && (
          <div
            className={cn(
              `absolute right-0 top-0 -translate-y-[35%] translate-x-[35%]`,
              className
            )}
          >
            <ClickableDiv
              className="text-text600 flex cursor-pointer select-none items-center justify-center hover:brightness-125"
              onClick={onClose}
            >
              <svg
                className="rounded-full"
                fill="none"
                height="36"
                viewBox="0 0 36 36"
                width="36"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="18" cy="18" fill="#6A73B5" r="18" />
                <path
                  d="M14 14L22 22"
                  stroke="#E2E4F5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
                <path
                  d="M22 14L14 22"
                  stroke="#E2E4F5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                />
              </svg>
            </ClickableDiv>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export const findShowcaseTarget = (targetId: string, showCaseId: string) => {
  return document.querySelector(
    `[data-showcase-target-id="${targetId}"][data-showcase-id="${showCaseId}"]`
  );
};

export const ShowcaseTarget: FC<{
  children: React.ReactNode;
  targetId: string;
  showCaseId: string;
}> = ({ children, targetId, showCaseId }) => {
  return (
    <Slot data-showcase-target-id={targetId} data-showcase-id={showCaseId}>
      {children}
    </Slot>
  );
};

const SPOTLIGHT_SPACE = 8;
export const ShowcaseSpotlight: FC<{
  showCaseId: string;
  targetId: string;
}> = ({ showCaseId, targetId }) => {
  const currentTarget = useMemo(
    () => findShowcaseTarget(targetId, showCaseId),
    [showCaseId, targetId]
  );

  const [rect, setRect] = useState(currentTarget?.getBoundingClientRect());

  const updateRect = useCallback(() => {
    if (currentTarget) {
      setRect(currentTarget.getBoundingClientRect());
    }
  }, [currentTarget]);

  useMutationObserver(currentTarget, updateRect);
  useEffect(() => {
    updateRect();
  }, [updateRect]);

  if (!currentTarget || !rect) return null;

  return (
    <div
      className="drop-shadow-card-2 fixed rounded-md border-2 border-[#00BFFF] bg-[#12EEB1]/10"
      style={{
        top: rect.top - SPOTLIGHT_SPACE,
        left: rect.left - SPOTLIGHT_SPACE,
        backgroundColor: 'gray',
        width: rect.width + SPOTLIGHT_SPACE * 2,
        height: rect.height + SPOTLIGHT_SPACE * 2,
      }}
    ></div>
  );
};

export const ShowcaseSpotlightContainer: FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  return createPortal(
    <div
      className="z-showcaseSpotlight fixed inset-0 h-dvh w-dvw"
      style={
        children
          ? {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              mixBlendMode: 'hard-light',
            }
          : {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }
      }
    >
      {children}
    </div>,
    document.body
  );
};
